/**
 * Rules Generator Service
 *
 * Generates IDE-specific rule files from preset bundles
 * Supports: Cursor, Windsurf, Antigravity, Claude Code, AGENTS.md, Gemini CLI, Codex
 */

import { IDE_FORMATS, JAI1_BASE_RULE, type IdeFormat } from '../config/ide-formats.js';
import { downshiftHeadings } from '../utils/heading-utils.js';

export interface PresetFile {
    filename: string;
    content: string;
}

export interface PresetBundle {
    preset: {
        slug: string;
        name: string;
        description: string;
        version: string;
        tags: string[];
        stack: Record<string, string | null>;
        files: {
            required: string[];
            optional: string[];
            template: string[];
        };
        output?: {
            agentsMd?: boolean;
            ideFolder?: string;
        };
    };
    files: Record<string, string>;
}

export interface GeneratedFile {
    path: string;
    content: string;
    description: string;
}

export class RulesGeneratorService {
    /**
     * Generate files for a specific IDE format
     */
    generateForIde(bundle: PresetBundle, ideId: string): GeneratedFile[] {
        const format = IDE_FORMATS[ideId];
        if (!format) {
            throw new Error(`Unknown IDE format: ${ideId}`);
        }

        switch (ideId) {
            case 'cursor':
                return this.generateCursorFiles(bundle, format);
            case 'windsurf':
                return this.generateWindsurfFiles(bundle, format);
            case 'antigravity':
                return this.generateAntigravityFiles(bundle, format);
            case 'claude':
                return this.generateClaudeFiles(bundle, format);
            case 'agentsmd':
                return this.generateAgentsMd(bundle, format);
            case 'codex':
                return this.generateAgentsMd(bundle, format);
            case 'gemini':
                return this.generateGeminiMd(bundle, format);
            default:
                throw new Error(`Unsupported IDE format: ${ideId}`);
        }
    }

    /**
     * Generate Cursor format (.mdc files)
     */
    private generateCursorFiles(bundle: PresetBundle, format: IdeFormat): GeneratedFile[] {
        const files: GeneratedFile[] = [];

        // Convert all preset files (preset includes its own base rule)
        for (const [filename, content] of Object.entries(bundle.files)) {
            files.push({
                path: `${format.rulesPath}/${filename}`,
                content: content, // Already in .mdc format
                description: `Preset rule: ${filename}`,
            });
        }

        return files;
    }

    /**
     * Generate Windsurf format (.md files with trigger metadata)
     */
    private generateWindsurfFiles(bundle: PresetBundle, format: IdeFormat): GeneratedFile[] {
        const files: GeneratedFile[] = [];

        // Convert all preset files (preset includes its own base rule)
        for (const [filename, content] of Object.entries(bundle.files)) {
            const converted = this.convertToWindsurf(content, filename);
            const newFilename = filename.replace(/\.mdc$/, '.md');

            files.push({
                path: `${format.rulesPath}/${newFilename}`,
                content: converted,
                description: `Preset rule: ${newFilename}`,
            });
        }

        return files;
    }

    /**
     * Convert Cursor format to Windsurf format
     */
    private convertToWindsurf(content: string, filename: string): string {
        // Parse frontmatter
        const frontmatterMatch = content.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
        if (!frontmatterMatch) {
            return content;
        }

        const [, frontmatter, body] = frontmatterMatch;
        const parsed = this.parseFrontmatter(frontmatter);

        // Determine trigger
        let trigger = 'always';
        if (parsed.alwaysApply === 'true' || parsed.alwaysApply === true) {
            trigger = 'always';
        } else if (parsed.globs && parsed.globs.length > 0) {
            trigger = parsed.globs.join(', ');
        }

        // Build Windsurf frontmatter
        const windsurfFrontmatter = `---
trigger: ${trigger}
---`;

        return `${windsurfFrontmatter}\n\n${body.trim()}\n`;
    }

    /**
     * Generate Antigravity format (.md files with trigger metadata + actual content)
     */
    private generateAntigravityFiles(bundle: PresetBundle, format: IdeFormat): GeneratedFile[] {
        const files: GeneratedFile[] = [];

        // Convert all preset files (preset includes its own base rule)
        for (const [filename, content] of Object.entries(bundle.files)) {
            const converted = this.convertToAntigravity(content, filename);
            const newFilename = filename.replace(/\.mdc$/, '.md');

            files.push({
                path: `${format.rulesPath}/${newFilename}`,
                content: converted,
                description: `Preset rule: ${newFilename}`,
            });
        }

        return files;
    }

    /**
     * Convert Cursor format to Antigravity format
     */
    private convertToAntigravity(content: string, filename: string): string {
        // Parse frontmatter
        const frontmatterMatch = content.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
        if (!frontmatterMatch) {
            return content;
        }

        const [, frontmatter, body] = frontmatterMatch;
        const parsed = this.parseFrontmatter(frontmatter);

        // Determine trigger
        let trigger = 'always_on';
        if (parsed.alwaysApply === 'true' || parsed.alwaysApply === true) {
            trigger = 'always_on';
        }

        // Build Antigravity frontmatter with actual content (no @reference)
        const antigravityFrontmatter = `---
trigger: ${trigger}
---`;

        return `${antigravityFrontmatter}\n\n${body.trim()}\n`;
    }

    /**
     * Generate Claude Code format (.md files with paths metadata)
     */
    private generateClaudeFiles(bundle: PresetBundle, format: IdeFormat): GeneratedFile[] {
        const files: GeneratedFile[] = [];

        // Convert all preset files (preset includes its own base rule)
        for (const [filename, content] of Object.entries(bundle.files)) {
            const converted = this.convertToClaude(content, filename);
            const newFilename = filename.replace(/\.mdc$/, '.md');

            files.push({
                path: `${format.rulesPath}/${newFilename}`,
                content: converted,
                description: `Preset rule: ${newFilename}`,
            });
        }

        return files;
    }

    /**
     * Convert Cursor format to Claude format
     */
    private convertToClaude(content: string, filename: string): string {
        // Parse frontmatter
        const frontmatterMatch = content.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
        if (!frontmatterMatch) {
            return content;
        }

        const [, frontmatter, body] = frontmatterMatch;
        const parsed = this.parseFrontmatter(frontmatter);

        // Build Claude frontmatter
        let metadata = `---
description: ${parsed.description || 'Rule'}`;

        if (parsed.globs && parsed.globs.length > 0) {
            // Convert globs to paths
            const paths = parsed.globs.map((g: string) => g.replace(/[\[\]"]/g, '')).join(', ');
            metadata += `\npaths: ${paths}`;
        }

        metadata += '\n---';

        return `${metadata}\n\n${body.trim()}\n`;
    }

    /**
     * Generate AGENTS.md file (single merged file)
     */
    private generateAgentsMd(bundle: PresetBundle, format: IdeFormat): GeneratedFile[] {
        const sections: string[] = [];

        // Header
        sections.push('# AGENTS.md\n');
        sections.push(`<!-- Generated by jai1 rules - Preset: ${bundle.preset.slug} v${bundle.preset.version} -->\n`);

        // Extract content from each file (remove frontmatter)
        const fileOrder = [
            '00-jai1.mdc',
            '01-project.mdc',
            '02-standards.mdc',
            '03-frontend.mdc',
            '04-backend.mdc',
            '05-testing.mdc',
            '06-workflow.mdc',
            '07-deployment.mdc',
            '08-database.mdc',
            '09-custom.mdc',
        ];

        for (const filename of fileOrder) {
            if (bundle.files[filename]) {
                const content = bundle.files[filename];
                const withoutFrontmatter = this.removeFrontmatter(content);

                if (withoutFrontmatter.trim()) {
                    sections.push(downshiftHeadings(withoutFrontmatter.trim()));
                    sections.push('\n');
                }
            }
        }

        const agentsMd = sections.join('\n');

        return [
            {
                path: 'AGENTS.md',
                content: agentsMd,
                description: 'Single AGENTS.md file with all rules',
            },
        ];
    }

    /**
     * Generate Gemini CLI format (GEMINI.md that references AGENTS.md)
     */
    private generateGeminiMd(bundle: PresetBundle, format: IdeFormat): GeneratedFile[] {
        const content = `# Gemini Instructions

@AGENTS.md

You are an AI coding assistant integrated with the Jai1 Framework.
Follow all instructions and patterns defined in AGENTS.md above.

## Additional Gemini-Specific Guidelines

- Use the context from AGENTS.md for all coding decisions
- Apply the Jai1 Framework patterns consistently
- Reference skills using the @jai1-[skill-name] syntax
- Maintain consistency with project standards

<!-- Generated by jai1 rules - Preset: ${bundle.preset.slug} v${bundle.preset.version} -->
`;

        return [
            {
                path: 'GEMINI.md',
                content: content,
                description: 'Gemini CLI instructions file',
            },
        ];
    }

    /**
     * Remove frontmatter from content
     */
    private removeFrontmatter(content: string): string {
        return content.replace(/^---\n[\s\S]*?\n---\n/, '');
    }

    /**
     * Parse YAML frontmatter into object
     */
    private parseFrontmatter(frontmatter: string): Record<string, any> {
        const result: Record<string, any> = {};
        const lines = frontmatter.split('\n');

        for (const line of lines) {
            const match = line.match(/^(\w+):\s*(.+)$/);
            if (match) {
                const [, key, value] = match;

                // Handle arrays (e.g., globs: ["**/*"])
                if (value.startsWith('[') && value.endsWith(']')) {
                    result[key] = value
                        .slice(1, -1)
                        .split(',')
                        .map((v) => v.trim().replace(/^["']|["']$/g, ''));
                } else if (value === 'true' || value === 'false') {
                    result[key] = value === 'true';
                } else {
                    result[key] = value.replace(/^["']|["']$/g, '');
                }
            }
        }

        return result;
    }

    /**
     * Get all IDE IDs that can be generated
     */
    getAvailableIdes(): string[] {
        return Object.keys(IDE_FORMATS);
    }

    /**
     * Check if IDE format exists
     */
    isValidIde(ideId: string): boolean {
        return ideId in IDE_FORMATS;
    }

    /**
     * Get IDE format details
     */
    getIdeFormat(ideId: string): IdeFormat | undefined {
        return IDE_FORMATS[ideId];
    }

    /**
     * Resolve IDE dependencies
     * For example, Gemini requires AGENTS.md
     */
    resolveIdeDependencies(ides: string[]): string[] {
        const resolved = new Set<string>(ides);

        for (const ide of ides) {
            const format = IDE_FORMATS[ide];
            if (format?.dependencies) {
                format.dependencies.forEach((dep) => resolved.add(dep));
            }
        }

        return Array.from(resolved);
    }
}
